import cx from 'classnames';
import React from 'react';
import useAppState from '../../../state/appState';
import { Teeth as TeethType, FormValues, FormConfig } from '@topdanmark-onlinesolutions/claim-form-types';
import TEETH_LIST from './teeth.json';
import styles from './teeth.module.css';
import { getClaimFormSubmitValue } from '../../../helpers/getClaimFormSubmitValue';
import { SummaryRowTemplate } from '../../../helpers/templates';
import { getElementFromElementId } from '../../../helpers/getElementFromElementId';
import { getValue } from '../../values/Get';

const Teeth = ({ caption, elementId }: TeethType) => {
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const onChangeValue = (value: string) => {
        if (formValueList.includes(value)) {
            if (formValueList.length === 1) {
                setFormValue(elementId, undefined);
            } else {
                setFormValue(elementId, [...formValueList.filter((id: string) => id !== value)]);
            }
        } else {
            setFormValue(elementId, [...formValueList.concat(value)]);
        }
    };

    const formValueList: string[] = formValues[elementId] ?? [];

    return <TeethSvg formValueList={formValueList} onChangeValue={onChangeValue} />;
};

export const TeethSvg = ({
    formValueList,
    onChangeValue
}: {
    formValueList: string[];
    onChangeValue: (v: string) => void;
}) => (
    <div className={styles.svg}>
        <svg version="1.1" viewBox="0 0 75 110" role={'group'}>
            <title>Tandsæt</title>
            <RightTextSvg />
            <LeftTextSvg />
            <PalateSvg />
            <TongueSvg />
            <FloorSvg />
            <g role="list">
                {TEETH_LIST.map(
                    (
                        {
                            value,
                            svg: {
                                tooth: [fill, stroke],
                                label
                            }
                        },
                        index
                    ) => {
                        const isActive = formValueList.includes(value);
                        return (
                            <React.Fragment key={index}>
                                <g role="listitem">
                                    <a
                                        onClick={(e) => {
                                            e.preventDefault();
                                            onChangeValue(value);
                                        }}
                                        tabIndex={0}
                                        href={`#tooth-${value}`}
                                        id={`tooth-${value}`}
                                        role="button"
                                        className={cx(styles.optionTarget, isActive && styles.optionTargetActive)}
                                        aria-label={value}
                                        aria-pressed={isActive}
                                    >
                                        <path
                                            className={styles.option}
                                            d={fill}
                                            fill={isActive ? '#414141' : '#fff'}
                                            tabIndex={-1}
                                        />
                                        {/* tabInde x -1 is to hide the outline when user clicks, but show outline when user tab to this tooth.
                                    See https://www.kizu.ru/keyboard-only-focus/
                                    */}
                                    </a>
                                    <path d={stroke} fill="#75bee9" />
                                    {label.map((p, index) => (
                                        <path d={p} key={index} />
                                    ))}
                                </g>
                            </React.Fragment>
                        );
                    }
                )}
            </g>
        </svg>
    </div>
);

const PalateSvg = React.memo(() => (
    <path
        fill="#dceef7"
        d="M16.14,50.44c.05-1.16-.21-2.29-.07-3.47s-.75-2-2.22-2.57c1.95-.23,2.21-1.36,2.07-2.78a3.87,3.87,0,0,1,.1-1.23,2.38,2.38,0,0,0-1.24-2.73c1.48-.48,3-.8,3-2.87,0-1.72-1-2.75-2.59-3.61a3.46,3.46,0,0,0,3.14-2.87c.43-1.71-.6-2.73-1.92-3.91,1.76.43,3.15.36,4.07-1.07a3.64,3.64,0,0,0-.36-4.84c.46-.18.77,0,1.08.11,2,.44,3-.3,3.13-2.37a17.22,17.22,0,0,0-.45-3.72,25,25,0,0,0,2.29,1.9c1.92,1.11,3.42.54,4.11-1.58a8.24,8.24,0,0,0,.36-2.77c1.13,1.49,1.92,3.27,4,3.06s2.55-2.07,3.13-3.83c.61,1.71,1,3.61,3.14,3.82S43.68,10.5,44.7,9c.13.86.23,2.77.42,3.5.63,2.52,2.28,3.14,4.48,1.74.7-.45,1.21-1.1,2-1.72-.08,1.18-.44,2.13-.43,3.15,0,2.72,1,3.48,3.56,2.85a.65.65,0,0,1,.64,0,3.64,3.64,0,0,0-.28,4.9c.91,1.35,2.28,1.42,4,1-1.3,1.19-2.36,2.21-1.92,3.91a3.36,3.36,0,0,0,3.31,2.85c-2.2,1-3.06,2.46-2.68,4.42.28,1.44,1.61,1.74,2.9,2.07a2.48,2.48,0,0,0-1.2,3,4.63,4.63,0,0,1,.06,1.24c0,1.84,0,1.88,1.84,2.6A2.77,2.77,0,0,0,59.45,48a9.16,9.16,0,0,1-.15,2.46,49.33,49.33,0,0,0-9.72-5A15.13,15.13,0,0,0,40.86,45a14,14,0,0,1-7.07-.06,5,5,0,0,0-1.33-.13,10.25,10.25,0,0,0-3,0c-3.24.18-6,1.63-8.79,3.12C19.11,48.71,17.64,49.59,16.14,50.44Z"
    />
));

const TongueSvg = React.memo(() => (
    <path
        d="M19.67,61.37c5.14-4,16.48-6.35,18.17,2.62.09.45.75.25.68-.19,0-.29-.63-3.05-.39-3.59C39,58.28,43,57.5,44.87,57.34c2.82-.25,9,.08,11.23,2.32.32.33.82-.17.5-.5C53.53,56,36.13,54.29,37.53,61.57c.63,3.24.72,0-.21-1.22a8.22,8.22,0,0,0-4.1-2.56c-5.21-1.87-9.84-.19-14.06,3.08-.36.28.14.78.5.5Z"
        fill="#75bee9"
    ></path>
));

const FloorSvg = React.memo(() => (
    <path
        d="M64,59.24c-1.6,2.13-1.07,5.1,1.39,7.37a4.82,4.82,0,0,0-2.94,3.85,5.69,5.69,0,0,0,1.61,5c-1.89,0-3.58.26-4.16,2.26a5.91,5.91,0,0,0,1.65,6c-1,.17-1.89.26-2.73.46-1.51.36-1.92,1.27-1.57,3a19,19,0,0,0,1.7,3.84c-1.1-.3-2-.64-3-.79-2.12-.34-3.18,1-2.78,3.47.17,1.06.43,2.11.69,3.37-1.21-.45-2,2.51-3,2.14-1.18-.43-1.79-3.77-2.22-2.55s-.16,2.64-.36,4c-.55-.6-1.08-1.2-1.64-1.77-1.28-1.28-2.44-.83-2.82,1.11-.21,1.07-.31,2.17-.47,3.38-.37-.87-.67-1.67-1.05-2.42s-.95-1.54-1.77-1.49-1.39.95-1.68,1.84a20.72,20.72,0,0,0-1,2.45,8,8,0,0,0-1-2.94c-.86-1.85-2-2-3.08-.27-.42.66-.73,1.42-1.17,2.31-.51-1-.27-2-.47-2.82-.52-2.31-.39-2.74-1.87-1.06-.48.54-2.15,1.17-2.79,2,0-1.29.06-2.3,0-3.31-.09-2.64-1.11-3.37-3.16-2.22A28.84,28.84,0,0,0,22,97.19a36.17,36.17,0,0,0,1-3.91c.35-2.58-.75-3.8-2.95-3.39-.88.17-1.75.44-2.75.7a30.3,30.3,0,0,0,1.45-2.85c1-2.5.45-3.64-1.8-4.19-.59-.15-1.2-.2-2-.32,1.16-1.66,2.19-3.29,1.84-5.46-.3-1.84-1.57-2.73-4.24-2.93a5.76,5.76,0,0,0,1.6-4.94,4,4,0,0,0-3-3.81,5.12,5.12,0,0,0,2.37-3.28c.25-1.34.62-2.75-.42-3.92,1.37-1.06,1.65-1,2.28.77l1.65,4.58a3.89,3.89,0,0,1,1.13,1.85c1.63,5.24,3.62,10.31,5.31,15.53C25.06,87.15,28.14,91,33,91.92a23.48,23.48,0,0,0,11-.26c3.4-1,5.65-3.94,7.57-7.21.43-.73.8-1.51,1.21-2.26a100.54,100.54,0,0,0,4.67-12.82c.27-.9.28-1.85.51-2.75a3.12,3.12,0,0,1,.85-1.86,12.34,12.34,0,0,1,2-5C62,58.18,62.75,58.06,64,59.24Z"
        fill="#dceef7"
    ></path>
));

const RightTextSvg = React.memo(() => (
    <>
        {/* H */}
        <path d="M3.72,4.83V3.09H1.89V4.83H1.48V1.15h.41V2.72H3.72V1.15h.41V4.83Z"></path>
        {/* ø */}
        <path d="M6.09,4.88a1.15,1.15,0,0,1-.45-.08l-.14.29H5.15l.22-.45A1.46,1.46,0,0,1,4.9,3.45C4.9,2.52,5.44,2,6.11,2a1.23,1.23,0,0,1,.47.09l.14-.29h.35l-.22.46a1.46,1.46,0,0,1,.45,1.17C7.29,4.36,6.76,4.88,6.09,4.88Zm0-2.54c-.48,0-.81.37-.81,1.1a1.34,1.34,0,0,0,.24.86l.9-1.88A.76.76,0,0,0,6.1,2.34Zm.58.27-.9,1.88a.77.77,0,0,0,.31.06c.48,0,.81-.38.81-1.11A1.32,1.32,0,0,0,6.68,2.61Z"></path>
        {/* j */}
        <path d="M7.62,5.84l-.13-.29C8,5.39,8,5.26,8,4.76V2.07h.39V4.76C8.44,5.39,8.31,5.67,7.62,5.84Zm.62-4.26a.28.28,0,1,1,.27-.28A.28.28,0,0,1,8.25,1.58Z"></path>
        {/* r */}
        <path d="M10.74,2.48a.78.78,0,0,0-.41-.1c-.38,0-.6.31-.6.83V4.83H9.33V2.07h.39v.34a.81.81,0,0,1,.68-.4,1.06,1.06,0,0,1,.43.07Z"></path>
        {/* e */}
        <path d="M13.55,3.58H11.71c0,.59.32,1,.77,1a1.23,1.23,0,0,0,.76-.26l.18.28a1.63,1.63,0,0,1-1,.32c-.5,0-1.12-.37-1.12-1.45,0-.88.48-1.42,1.14-1.42s1.11.57,1.11,1.35A1.23,1.23,0,0,1,13.55,3.58Zm-1.1-1.23c-.41,0-.69.29-.74.91H13.2C13.17,2.73,12.94,2.35,12.45,2.35Z"></path>
    </>
));

const LeftTextSvg = React.memo(() => (
    <>
        {/* V */}
        <path d="M58,4.83H57.6L56.35,1.15h.43l.82,2.51c.07.22.19.59.23.72,0-.13.16-.52.23-.73l.82-2.5h.42Z"></path>
        {/* e */}
        <path d="M62.09,3.58H60.26c0,.59.31,1,.77,1a1.23,1.23,0,0,0,.76-.26l.18.28a1.63,1.63,0,0,1-1,.32c-.5,0-1.12-.37-1.12-1.45C59.86,2.56,60.34,2,61,2s1.11.57,1.11,1.35A1.18,1.18,0,0,1,62.09,3.58ZM61,2.35c-.41,0-.69.29-.74.91h1.49C61.71,2.73,61.48,2.35,61,2.35Z"></path>
        {/* n */}
        <path d="M64.57,4.83V3.21c0-.59-.21-.86-.65-.86s-.68.26-.68.86V4.83h-.39V2.07h.39v.34A.89.89,0,0,1,64,2c.58,0,1,.34,1,1.13V4.83Z"></path>
        {/* s */}
        <path d="M66.58,4.88a1.55,1.55,0,0,1-1-.35l.23-.29a1.13,1.13,0,0,0,.75.3c.38,0,.56-.16.56-.43s-.16-.35-.65-.56-.78-.4-.78-.84.36-.7.86-.7a1.36,1.36,0,0,1,.88.29l-.22.29a1,1,0,0,0-.69-.25c-.29,0-.46.14-.46.34s.14.33.61.54.82.39.82.88S67.06,4.88,66.58,4.88Z"></path>
        {/* t */}
        <path d="M68.77,2.4V4.2c0,.25.07.33.28.33a.92.92,0,0,0,.39-.08l0,.34a1.24,1.24,0,0,1-.51.09c-.34,0-.52-.17-.52-.62V2.4H68V2.07h.34V1.22L68.77,1V2.07h.68V2.4Z"></path>
        {/* r */}
        <path d="M71.62,2.48a.78.78,0,0,0-.41-.1c-.38,0-.6.31-.6.83V4.83h-.39V2.07h.39v.34a.81.81,0,0,1,.68-.4,1.06,1.06,0,0,1,.43.07Z"></path>
        {/* e */}
        <path d="M74.44,3.58H72.6c0,.59.32,1,.77,1a1.23,1.23,0,0,0,.76-.26l.18.28a1.63,1.63,0,0,1-1,.32c-.5,0-1.12-.37-1.12-1.45,0-.88.48-1.42,1.14-1.42s1.11.57,1.11,1.35A1.23,1.23,0,0,1,74.44,3.58Zm-1.1-1.23c-.41,0-.69.29-.74.91h1.49C74.05,2.73,73.82,2.35,73.34,2.35Z"></path>
    </>
));

Teeth.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string) => {
    const element = getElementFromElementId(formConfig, elementId);
    const { summaryCaption } = element;
    const value = getValue(formConfig, formValues, elementId);
    if (value === undefined) {
        return <></>;
    }
    const valueHuman = Teeth.getValueHuman(formConfig, formValues, elementId);

    // const display = <TeethSvg formValueList={value} onChangeValue={() => {}} />;

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={valueHuman} />;
};

Teeth.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string) => {
    const valueObject = getValue(formConfig, formValues, elementId);

    if (valueObject && valueObject.label !== '') {
        return true;
    }
    return false;
};

Teeth.getValueOrDefault = (formConfig: FormConfig, formValues: FormValues, elementId: string) => {
    const value: string[] = Teeth.getValue(formConfig, formValues, elementId);

    return value;
};

Teeth.getValue = (formConfig: FormConfig, formValues: FormValues, elementId: string) => {
    const formValueList: string[] = formValues[elementId] ?? '';
    return formValueList;
};

Teeth.getValueHuman = (formConfig: FormConfig, formValues: FormValues, elementId: string) => {
    const value: string[] = Teeth.getValue(formConfig, formValues, elementId);

    return value.length === 1 ? value[0] : [value.slice(0, -1).join(', '), value[value.length - 1]].join(' og ');
};

Teeth.getValueSubmit = (formConfig: FormConfig, formValues: FormValues, elementId: string, caption: string) => {
    const value: string[] = Teeth.getValue(formConfig, formValues, elementId);

    const valueStringList = value.toString();

    const valueId = { string: valueStringList };
    return getClaimFormSubmitValue('teeth', elementId, caption, valueStringList, valueId);
};

export default Teeth;
